import type { ReactNode } from 'react'

export interface RadioCardProps {
  readonly name: string
  readonly value: string
  readonly checked: boolean
  readonly onChange: (value: string) => void
  readonly label: ReactNode
  readonly description?: ReactNode
  /** Trailing badge, e.g. a "Default" Chip on a saved address (UX spec §2.2). */
  readonly chip?: ReactNode
  /** Trailing content pinned to the far right of the label row (e.g. §2's
   * "$59.36" / "Billed by carrier" next to the option name, UX spec §2.3 —
   * the prototype's `co.prepaidCost`). Rendered outside `radio-card-label`
   * so it never wraps with the label text. */
  readonly trailing?: ReactNode
  readonly disabled?: boolean
  /** Progressive-disclosure content rendered below the label ONLY while
   * checked (UX spec §2.3 — own-account's carrier/account-ref fields). */
  readonly children?: ReactNode
  readonly className?: string
}

/**
 * Card-shaped selectable option (components.md "RadioCard") built on a
 * native `<input type="radio">` wrapped by a full-card `<label>` — roving
 * tabindex, arrow-key navigation, and correct AT announcement come for
 * free, with zero hand-rolled `role="radio"` ARIA. Callers wrap a set of
 * RadioCards in a `<fieldset>`/`<legend>` for the accessible group name (UX
 * spec §4.1) — this primitive renders one option, not the group.
 *
 * Checked state is signaled redundantly (never color-alone, WCAG 1.4.1):
 * the checked CSS class flips border + background tint AND the radio dot
 * fill together (see ui.css ".radio-card-checked").
 */
export function RadioCard({
  name,
  value,
  checked,
  onChange,
  label,
  description,
  chip,
  trailing,
  disabled,
  children,
  className,
}: RadioCardProps) {
  const classes = [
    'radio-card',
    checked && 'radio-card-checked',
    disabled && 'radio-card-disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <label className={classes}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
        className="radio-card-input"
      />
      <span className="radio-card-body">
        <span className="radio-card-heading-row">
          <span className="radio-card-label">{label}</span>
          {chip}
          {trailing && <span className="radio-card-trailing">{trailing}</span>}
        </span>
        {description && <span className="radio-card-description">{description}</span>}
        {checked && children && <div className="radio-card-revealed">{children}</div>}
      </span>
    </label>
  )
}
